import type { RosterPlayerListItem } from './mapRosterPlayerToForm'

/** Which of the three roster form modes the route params select. */
export type RosterMode = 'create' | 'addExisting' | 'edit'

/**
 * Resolve the form mode from the screen's params. `editPlayer` (a roster row)
 * always means edit, even if a playerId is also present; a bare playerId means
 * add-existing (copy); neither means create-new. Pure — unit-testable.
 */
export const resolveRosterMode = (params: {
  playerId?: string
  editPlayer?: RosterPlayerListItem
}): RosterMode => {
  if (params.editPlayer) {
    return 'edit'
  }
  if (params.playerId) {
    return 'addExisting'
  }
  return 'create'
}

/**
 * Whether the roster player's email field must be locked. Email is the player's
 * settled identity once it's confirmed, so it's read-only when:
 *   - add-existing (copy) — the player's profile is owned elsewhere, OR
 *   - editing a player who has ACCEPTED their invite (is_accepted).
 *
 * An invited-but-unaccepted player (is_invited && !is_accepted) keeps email
 * editable so the inviter can still correct a wrong address. Create-new is
 * always editable. Pure — unit-testable.
 */
export const isRosterEmailLocked = (params: {
  mode: RosterMode
  editPlayer?: RosterPlayerListItem
}): boolean => {
  if (params.mode === 'addExisting') {
    return true
  }
  if (params.mode === 'edit') {
    return Boolean(params.editPlayer?.is_accepted)
  }
  return false
}

/**
 * Whether First/Last Name must be locked (read-only). Names are only locked in
 * add-existing (copy) mode — you're pulling in another team's player record, so
 * renaming it there is disallowed. Editing your own roster player (even an
 * accepted-invite one) and creating a new player both keep names editable, so a
 * typo can always be corrected. Pure — unit-testable.
 *
 * NOTE: this is intentionally NOT tied to `isRosterEmailLocked` — email is the
 * settled identity and stays locked for accepted players; names do not.
 */
export const isRosterNamesLocked = (mode: RosterMode): boolean =>
  mode === 'addExisting'

/**
 * Whether the Canlan Salesforce ID field must be locked (read-only). Admins can
 * SET it on a brand-new player and CORRECT it while editing one of their own
 * roster players. Only add-existing (copy) locks it — that player's record is
 * owned by another team, so its Salesforce ID is display-only there.
 * Pure — unit-testable.
 */
export const isRosterSalesforceIdLocked = (mode: RosterMode): boolean =>
  mode === 'addExisting'
